/**
 * Keeping the installed app on the current version.
 *
 * The service worker was registered by the one-line script vite-plugin-pwa
 * injects when nothing else claims the job:
 *
 *   window.addEventListener('load', () => navigator.serviceWorker.register('/sw.js'))
 *
 * which registers once and never asks again. A browser tab hides that: every
 * reload is a navigation, and a navigation re-fetches sw.js. An installed app
 * on a phone has no such moment. It is opened, backgrounded, resumed, opened
 * again — and none of that is a navigation, so the worker is only re-fetched
 * on Chrome's own 24-hour check. In between, `navigateFallback` keeps serving
 * the precached index.html, which names the bundle from whenever the worker
 * was installed. The app is not failing to reload; it is never being told
 * there is anything to reload to.
 *
 * So the check is tied to the moment that actually happens on a phone:
 * bringing the app back to the front. `registerType: 'autoUpdate'` does the
 * rest — the new worker skips waiting, claims the page, and `registerSW`
 * reloads once it has.
 *
 * The hourly timer is for the other half of the problem: a phone left open on
 * the kitchen counter all afternoon, which is neither hidden nor navigating.
 *
 * That still left gardeners on an old copy, clearing the browser's storage by
 * hand to get the new one: it depends on the browser noticing a new worker
 * and handing the page over. So there is a second check that depends on
 * nothing but a file (2026-10-02). Each build writes version.json beside the
 * app with its own id; on coming back to the app, and every fifteen minutes
 * while it is open, the app asks for that file, bypassing every cache, and
 * when the id is not its own it drops the old worker and its stored copy and
 * reloads, once for that build. A gardener never has to clear anything.
 *
 * Dropping the old worker also drops its notification subscription, so once
 * a worker is registered, app notifications a gardener had turned on are
 * renewed (shared/lib/pushNotifications renewPushSubscription).
 *
 * Who uses it: main.tsx, which calls startUpdateChecks once. __GROW_BUILD__
 * is the build's id, defined by vite.config, which also writes version.json.
 * Does nothing in development.
 *
 * @module pwaUpdate
 */

import { registerSW } from 'virtual:pwa-register';
import { renewPushSubscription } from '@/shared/lib/pushNotifications';
import {
  RELOADED_FOR_SESSION_KEY,
  SW_UPDATE_CHECK_MS,
  VERSION_CHECK_MS,
  VERSION_FIRST_CHECK_DELAY_MS,
} from '@/config/tunables/shared';

/** Long enough not to badger the network, short enough that a day's work is not missed. */
const CHECK_EVERY_MS = SW_UPDATE_CHECK_MS;

/** How often an open app asks whether a newer build is out. */
const VERSION_EVERY_MS = VERSION_CHECK_MS;

/** Where a reload for a build is remembered, so a server still sending the old app cannot loop it. */
const RELOADED_FOR = RELOADED_FOR_SESSION_KEY;

/**
 * Drops the old copy and loads the new one: the service worker and its
 * stored files go, then the page reloads from the server. Once per build: if
 * the server hands back the same old app (a deploy half done), it waits for
 * the next check rather than reloading again and again.
 *
 * @param build - The newer build's id, from version.json.
 */
async function loadNewer(build: string): Promise<void> {
  try {
    if (window.sessionStorage.getItem(RELOADED_FOR) === build) return;
    window.sessionStorage.setItem(RELOADED_FOR, build);
  } catch { /* storage refused: reload anyway, once this page */ }
  try {
    const workers = await navigator.serviceWorker?.getRegistrations?.() ?? [];
    await Promise.all(workers.map((worker) => worker.unregister()));
  } catch { /* none to drop */ }
  try {
    if (window.caches) await Promise.all((await caches.keys()).map((name) => caches.delete(name)));
  } catch { /* none to clear */ }
  window.location.reload();
}

/**
 * Asks the server which build is current (version.json, past every cache)
 * and loads it when it is not this one. Quiet when offline or when the file
 * cannot be read: the app carries on as it is.
 */
async function checkVersion(): Promise<void> {
  if (!navigator.onLine) return;
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return;
    const { build } = await res.json() as { build?: string };
    if (build && build !== __GROW_BUILD__) await loadNewer(build);
  } catch { /* offline, or no file: try again later */ }
}

/**
 * Keeps the app on the current build: the version check (always), and the
 * service worker's own updates (where there is one).
 */
export function startUpdateChecks(): void {
  // Not in development: the dev server has no version.json, and reloads itself.
  if (import.meta.env.DEV) return;
  // The version check, which needs no service worker: on coming back, and every fifteen minutes.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void checkVersion();
  });
  window.addEventListener('focus', () => { void checkVersion(); });
  window.setInterval(() => { void checkVersion(); }, VERSION_EVERY_MS);
  // And once soon after opening: an app opened from a stale copy hears at once.
  window.setTimeout(() => { void checkVersion(); }, VERSION_FIRST_CHECK_DELAY_MS);

  if (!('serviceWorker' in navigator)) return;

  const updateSW = registerSW({
    immediate: true,
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;

      // Notifications turned on here stay on after loadNewer dropped the old worker.
      void renewPushSubscription(registration);

      /**
       * Ask the server whether sw.js has changed.
       *
       * Guarded on `installing`: calling update() while an install is already
       * under way starts a second one, and on a slow connection that can mean
       * two downloads racing to claim the page.
       */
      const check = (): void => {
        if (registration.installing || !navigator.onLine) return;
        void registration.update();
      };

      // The moment a phone actually gives us: coming back to the app.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') check();
      });
      // And for the screen that is never backgrounded at all.
      window.setInterval(check, CHECK_EVERY_MS);
    },
  });

  // Nothing calls this yet: autoUpdate reloads on its own once the new worker
  // takes over. Kept so a "refresh now" prompt has something to call if we
  // ever decide the silent reload is too abrupt.
  void updateSW;
}
